import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Drawer,
    CircularProgress,
    Snackbar,
    Alert,
    Button,
    TextField,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import agentChatService from '../services/AgentChatService';

const DOC_PAGE_REF_RE = /\[([^\]]+):([a-f0-9]{20,})\/(\d{1,6})\]/gi;
const DINGBAT_CIRCLED_NUMS = ['❶', '❷', '❸', '❹', '❺', '❻', '❼', '❽', '❾', '❿'];

function formatSourceIndex(n) {
    if (n >= 1 && n <= DINGBAT_CIRCLED_NUMS.length) {
        return DINGBAT_CIRCLED_NUMS[n - 1];
    }
    return String(n);
}

function extractSourceReferences(text) {
    if (!text) return [];

    const refs = [];
    const regex = new RegExp(DOC_PAGE_REF_RE.source, 'gi');
    let match;
    while ((match = regex.exec(text)) !== null) {
        const page = Number.parseInt(match[3], 10);
        if (!Number.isFinite(page) || page <= 0) continue;
        refs.push({
            source: match[1],
            documentId: match[2],
            page,
            n: refs.length + 1,
        });
    }
    return refs;
}

function SourceMarker({ reference }) {
    const { t } = useTranslation();
    const label = t('agentConversation.sourceLabel', 'Source {{n}}: {{source}}, page {{page}}', {
        n: reference.n,
        source: reference.source,
        page: reference.page,
    });

    return (
        <Box
            component="span"
            title={label}
            aria-label={t('agentConversation.sourceAria', 'Source {{n}}', { n: reference.n })}
            sx={{
                display: 'inline',
                whiteSpace: 'nowrap',
                color: '#dc2626',
                fontWeight: 700,
                textDecoration: 'none',
                cursor: 'default',
            }}
        >
            {formatSourceIndex(reference.n)}
        </Box>
    );
}


function renderTextWithSourceMarkers(text, references, nextReferenceIndex) {
    const value = typeof text === 'string' ? text : text != null ? String(text) : '';
    if (!value) return value;

    const parts = [];
    const regex = new RegExp(DOC_PAGE_REF_RE.source, 'gi');
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(value)) !== null) {
        const page = Number.parseInt(match[3], 10);
        if (!Number.isFinite(page) || page <= 0) continue;

        if (match.index > lastIndex) {
            parts.push(value.slice(lastIndex, match.index));
        }

        const reference = references[nextReferenceIndex.current] || {
            source: match[1],
            documentId: match[2],
            page,
            n: nextReferenceIndex.current + 1,
        };
        nextReferenceIndex.current += 1;
        parts.push(<SourceMarker key={`source-${match.index}-${reference.n}`} reference={reference} />);
        lastIndex = regex.lastIndex;
    }

    if (lastIndex < value.length) {
        parts.push(value.slice(lastIndex));
    }

    return parts.length > 0 ? parts : value;
}

function renderMarkdownChildrenWithSourceMarkers(children, references, nextReferenceIndex) {
    if (typeof children === 'string') {
        return renderTextWithSourceMarkers(children, references, nextReferenceIndex);
    }

    if (Array.isArray(children)) {
        return children.map((child, i) =>
            typeof child === 'string' ? (
                <React.Fragment key={i}>{renderTextWithSourceMarkers(child, references, nextReferenceIndex)}</React.Fragment>
            ) : (
                child
            )
        );
    }

    return children;
}

function shouldShowInAgentConversation(message) {
    const messageType = String(message?.messageType || message?.sender || '').toLowerCase();
    const messageContent = String(message?.messageContent || message?.content || '').trim().toLowerCase();
    return !(messageType === 'system' && messageContent === 'agent disconnected');
}

function AgentConversation({ open, onClose, conversationId }) {
    const { t } = useTranslation();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [agentText, setAgentText] = useState('');
    const [uploading, setUploading] = useState(false);
    const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
    const [leaving, setLeaving] = useState(false);

    const messagesContainerRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchMessages = async () => {
            if (!open || !conversationId) return;
            try {
                setLoading(true);
                setError('');
                setUploading(false);
                const data = await agentChatService.getConversationMessages(conversationId);
                setMessages(Array.isArray(data) ? data.filter(shouldShowInAgentConversation) : []);
            } catch (e) {
                console.error('Error fetching agent chat messages', e);
                setError(t('agentConversation.errorLoad', 'Failed to load conversation'));
            } finally {
                setLoading(false);
            }
        };

        fetchMessages();
    }, [open, conversationId]);

    useLayoutEffect(() => {
        if (!open || loading) return;

        const container = messagesContainerRef.current;
        if (!container) return;

        container.scrollTop = container.scrollHeight;
    }, [messages, open, loading]);

    // Cleanup WebSocket on unmount or when conversation changes
    useEffect(() => {
        return () => {
            if (socket) {
                try {
                    socket.close();
                } catch (e) {
                    // ignore
                }
            }
        };
    }, [socket]);

    const handleCloseError = () => setError('');

    const disconnectAndClose = () => {
        try {
            if (socket) {
                socket.close();
            }
        } catch (e) {
            // ignore
        } finally {
            setIsConnected(false);
            setSocket(null);
        }

        if (typeof onClose === 'function') {
            onClose();
        }
    };

    const handleLeaveConversation = async () => {
        if (!conversationId) {
            setError('conversationId is missing');
            return;
        }

        const apiBase = (process.env.REACT_APP_CHATBOT_API_URL || process.env.CHATBOT_API_URL || '').replace(/\/+$/, '');
        if (!apiBase) {
            setError('Chatbot API URL is not configured');
            return;
        }

        try {
            setLeaving(true);
            setError('');
            const leaveUrl = `${apiBase}/conversations/${encodeURIComponent(conversationId)}/leave`;
            const resp = await fetch(leaveUrl, { method: 'POST' });
            if (!resp.ok) {
                console.error('Failed to leave agent conversation', resp.status);
                setError('Failed to leave conversation');
                return;
            }

            setLeaveConfirmOpen(false);
            disconnectAndClose();
        } catch (e) {
            console.error('Error leaving agent conversation', e);
            setError('Failed to leave conversation');
        } finally {
            setLeaving(false);
        }
    };

    const handleRequestClose = () => {
        if (isConnected) {
            setLeaveConfirmOpen(true);
            return;
        }
        if (typeof onClose === 'function') {
            onClose();
        }
    };

    const formatTimestamp = (ts) => {
        if (!ts) return '';
        return dayjs.unix(ts).format('YYYY/MM/DD - HH:mm');
    };

    const renderTextWithLinksPlain = (text) => {
        const value = typeof text === 'string' ? text : text != null ? String(text) : '';
        const urlRegex = /https?:\/\/[^\s]+/g;
        const parts = [];

        let lastIndex = 0;
        let match;
        while ((match = urlRegex.exec(value)) !== null) {
            const start = match.index;
            const matched = match[0];

            if (start > lastIndex) {
                parts.push(value.slice(lastIndex, start));
            }

            // Strip common trailing punctuation that shouldn't be part of the URL.
            let url = matched;
            let trailing = '';
            while (url.length > 0) {
                const lastChar = url.slice(-1);
                if (!')]}"\'.,;:!?'.includes(lastChar)) break;
                trailing = lastChar + trailing;
                url = url.slice(0, -1);
            }

            if (url) {
                parts.push(
                    <a
                        key={`link-${start}`}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                        {url}
                    </a>
                );
            } else {
                parts.push(matched);
            }

            if (trailing) {
                parts.push(trailing);
            }

            lastIndex = start + matched.length;
        }

        if (lastIndex < value.length) {
            parts.push(value.slice(lastIndex));
        }

        return parts;
    };

    const renderTextWithBracketLinks = (text) => {
        const value = typeof text === 'string' ? text : text != null ? String(text) : '';
        // Accept both markdown-style `[label](url)` and the looser `[label] (url)` seen in messages.
        const bracketLinkRegex = /\[([^\]]+)\]\s*\((https?:\/\/[^\s)]+)\)/g;
        const parts = [];

        let lastIndex = 0;
        let match;
        while ((match = bracketLinkRegex.exec(value)) !== null) {
            const start = match.index;
            const full = match[0];
            const label = match[1];
            const url = match[2];

            if (start > lastIndex) {
                parts.push(...renderTextWithLinksPlain(value.slice(lastIndex, start)));
            }

            parts.push(
                <a
                    key={`bracket-link-${start}`}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                    {label}
                </a>
            );

            lastIndex = start + full.length;
        }

        if (lastIndex < value.length) {
            parts.push(...renderTextWithLinksPlain(value.slice(lastIndex)));
        }

        return parts;
    };

    const handleSend = async () => {
        if (!conversationId) {
            setError(t('agentConversation.errorConversationIdMissing', 'conversationId is missing'));
            return;
        }
        if (!isConnected) {
            return;
        }

        const trimmed = agentText.trim();
        if (!trimmed) {
            return;
        }

        const apiBase = (process.env.REACT_APP_CHATBOT_API_URL || process.env.CHATBOT_API_URL || '').replace(/\/+$/, '');
        if (!apiBase) {
            setError(t('agentConversation.errorApiUrlNotConfigured', 'Chatbot API URL is not configured'));
            return;
        }

        const sendUrl = `${apiBase}/conversations/${encodeURIComponent(conversationId)}/messages`;
        try {
            const resp = await fetch(sendUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    messageContent: trimmed,
                    sender: 'agent',
                    to: 'user',
                }),
            });

            if (!resp.ok) {
                console.error('Failed to send agent message', resp.status);
                setError(t('agentConversation.errorSendMessage', 'Failed to send message'));
                return;
            }

            // Optimistically append the agent message to the local view
            setMessages((prev) => [
                ...prev,
                {
                    conversationHistoryId: `local-${Date.now()}`,
                    createdAt: Math.floor(Date.now() / 1000),
                    messageContent: trimmed,
                    messageType: 'agent',
                },
            ]);
            setAgentText('');
        } catch (e) {
            console.error('Error sending agent message', e);
            setError(t('agentConversation.errorSendMessage', 'Failed to send message'));
        }
    };

    const handlePickFile = () => {
        if (!isConnected || uploading) return;
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const sendAgentMessage = async (messageContent) => {
        if (!conversationId) {
            setError(t('agentConversation.errorConversationIdMissing', 'conversationId is missing'));
            return false;
        }

        const apiBase = (process.env.REACT_APP_CHATBOT_API_URL || process.env.CHATBOT_API_URL || '').replace(/\/+$/, '');
        if (!apiBase) {
            setError(t('agentConversation.errorApiUrlNotConfigured', 'Chatbot API URL is not configured'));
            return false;
        }

        const sendUrl = `${apiBase}/conversations/${encodeURIComponent(conversationId)}/messages`;
        const resp = await fetch(sendUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                messageContent,
                sender: 'agent',
                to: 'user',
            }),
        });

        return resp.ok;
    };

    const handleFileSelected = async (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;

        if (!isConnected) {
            setError(t('agentConversation.errorConnectFirst', 'Please connect first'));
            return;
        }

        const contentType = file.type || 'application/pdf';
        const isPdf = contentType === 'application/pdf' || String(file.name || '').toLowerCase().endsWith('.pdf');
        if (!isPdf) {
            setError(t('agentConversation.errorPdfOnly', 'Only PDF files are supported'));
            return;
        }

        const apiBase = (process.env.REACT_APP_CHATBOT_API_URL || process.env.CHATBOT_API_URL || '').replace(/\/+$/, '');
        if (!apiBase) {
            setError(t('agentConversation.errorApiUrlNotConfigured', 'Chatbot API URL is not configured'));
            return;
        }

        try {
            setUploading(true);
            setError('');

            // 1) presign
            const presignResp = await fetch(`${apiBase}/attachments/presign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    conversation_id: conversationId,
                    filename: file.name,
                    content_type: contentType,
                }),
            });

            if (!presignResp.ok) {
                console.error('Failed to presign attachment', presignResp.status);
                setError(t('agentConversation.errorStartUpload', 'Failed to start upload'));
                return;
            }

            const presignData = await presignResp.json();
            const uploadUrl = presignData?.upload;
            const s3Key = presignData?.s3_key;

            if (!uploadUrl || !s3Key) {
                setError(t('agentConversation.errorPresignResponse', 'Invalid presign response'));
                return;
            }

            // 2) PUT to S3
            const putResp = await fetch(uploadUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': contentType,
                },
                body: file,
            });

            if (!putResp.ok) {
                console.error('Failed to upload to S3', putResp.status);
                setError(t('agentConversation.errorUploadFile', 'Failed to upload file'));
                return;
            }

            // 3) complete
            const completeResp = await fetch(`${apiBase}/attachments/complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    s3_key: s3Key,
                    content_type: contentType,
                }),
            });

            if (!completeResp.ok) {
                console.error('Failed to complete attachment', completeResp.status);
                setError(t('agentConversation.errorFinalizeUpload', 'Failed to finalize upload'));
                return;
            }

            const completeData = await completeResp.json();
            const downloadUrl = completeData?.download;
            if (!downloadUrl) {
                setError(t('agentConversation.errorCompleteResponse', 'Invalid complete response'));
                return;
            }

            const messageContent = `Agent uploaded [${file.name}](${downloadUrl})`;
            const ok = await sendAgentMessage(messageContent);
            if (!ok) {
                setError(t('agentConversation.errorUploadSentFailed', 'Upload succeeded but failed to send message'));
                return;
            }

            // Optimistically append the agent message to the local view
            setMessages((prev) => [
                ...prev,
                {
                    conversationHistoryId: `local-upload-${Date.now()}`,
                    createdAt: Math.floor(Date.now() / 1000),
                    messageContent,
                    messageType: 'agent',
                },
            ]);
        } catch (err) {
            console.error('Attachment upload failed', err);
            setError(t('agentConversation.errorUploadFile', 'Failed to upload file'));
        } finally {
            setUploading(false);
        }
    };

    const handleConnect = async () => {
        if (!conversationId) {
            setError(t('agentConversation.errorConversationIdMissing', 'conversationId is missing'));
            return;
        }
        if (socket || isConnected) {
            return;
        }

        // First, notify backend that agent is joining this conversation
        const apiBase = (process.env.REACT_APP_CHATBOT_API_URL || process.env.CHATBOT_API_URL || '').replace(/\/+$/, '');
        if (!apiBase) {
            setError(t('agentConversation.errorApiUrlNotConfigured', 'Chatbot API URL is not configured'));
            return;
        }

        const joinUrl = `${apiBase}/conversations/${encodeURIComponent(conversationId)}/join`;
        try {
            const joinResponse = await fetch(joinUrl, { method: 'POST' });
            if (!joinResponse.ok) {
                console.error('Failed to join agent conversation', joinResponse.status);
                setError(t('agentConversation.errorJoinConversation', 'Failed to join conversation'));
                return;
            }
        } catch (e) {
            console.error('Error joining agent conversation', e);
            setError(t('agentConversation.errorJoinConversation', 'Failed to join conversation'));
            return;
        }

        const wsUrl = process.env.REACT_APP_WEBSOCKET_API_URL;
        if (!wsUrl) {
            setError(t('agentConversation.errorWebSocketUrlNotConfigured', 'WebSocket URL is not configured'));
            return;
        }

        try {
            const ws = new WebSocket(wsUrl);
            setSocket(ws);

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    const incoming = Array.isArray(data) ? data : [data];

                    const visibleIncoming = incoming.filter(shouldShowInAgentConversation);

                    setMessages((prev) => [
                        ...prev,
                        ...visibleIncoming.map((m) => {
                            const sender = (m.sender || '').toLowerCase();
                            // Map WebSocket payload shape to our internal message model
                            // WS: { message_type, content, sender }
                            return {
                                conversationHistoryId:
                                    m.conversationHistoryId || `ws-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                                createdAt: m.createdAt ?? Math.floor(Date.now() / 1000),
                                messageContent: m.content ?? m.messageContent ?? '',
                                messageType: sender || 'bot',
                            };
                        }),
                    ]);
                } catch (parseErr) {
                    console.error('Failed to parse WebSocket message', parseErr, event.data);
                }
            };

            ws.onopen = () => {
                setIsConnected(true);
                try {
                    ws.send(JSON.stringify({ conversationId, sender: 'agent' }));
                } catch (sendErr) {
                    console.error('Failed to send initial agent message', sendErr);
                }
            };

            ws.onerror = (event) => {
                console.error('WebSocket error', event);
                setError(t('agentConversation.errorWebSocketConnection', 'WebSocket connection error'));
            };

            ws.onclose = () => {
                setIsConnected(false);
                setSocket(null);
            };
        } catch (e) {
            console.error('Failed to open WebSocket', e);
            setError(t('agentConversation.errorOpenWebSocket', 'Failed to open WebSocket'));
        }
    };

    return (
        <>
            <Drawer anchor="right" open={open} onClose={handleRequestClose}>
                <Box sx={{ width: 420, p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {t('agentConversation.title', 'Agent Conversation')}
                    </Typography>
                    {loading ? (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : (
                        <>
                            <Box
                                ref={messagesContainerRef}
                                sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', pr: 1 }}
                            >
                                {messages.map((detail) => {
                                    const senderRole = (detail?.messageType || '').toLowerCase();
                                    const isUser = senderRole === 'user';
                                    const isAgent = senderRole === 'agent';
                                    const isBot = senderRole === 'bot' || (!isUser && !isAgent);
                                    const isRightSide = isUser; // only user messages on right
                                    const rawMessage = detail?.messageContent;
                                    const displayMessage =
                                        typeof rawMessage === 'string'
                                            ? rawMessage
                                            : rawMessage != null
                                                ? String(rawMessage)
                                                : '';
                                    const sourceReferences = isBot ? extractSourceReferences(displayMessage) : [];
                                    const nextSourceReferenceIndex = { current: 0 };
                                    return (
                                        <Box
                                            key={detail.conversationHistoryId}
                                            sx={{ display: 'flex', justifyContent: isRightSide ? 'flex-end' : 'flex-start', minWidth: 0 }}
                                        >
                                            <Box
                                                sx={{
                                                    bgcolor: isAgent
                                                        ? 'success.main'
                                                        : isUser
                                                            ? 'primary.main'
                                                            : 'grey.200',
                                                    color: isAgent || isUser ? 'common.white' : 'text.primary',
                                                    px: 1.5,
                                                    py: 1,
                                                    borderRadius: 2,
                                                    borderTopLeftRadius: isRightSide ? 8 : 0,
                                                    borderTopRightRadius: isRightSide ? 0 : 8,
                                                    maxWidth: '75%',
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                    boxShadow: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    component="div"
                                                    sx={{
                                                        whiteSpace: isBot ? 'normal' : 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        overflowWrap: 'anywhere',
                                                        '& a': {
                                                            color: isBot ? '#1976d2' : 'inherit',
                                                            fontWeight: 500,
                                                            textDecoration: 'underline',
                                                            textDecorationColor: isBot ? 'rgba(25,118,210,0.4)' : 'currentColor',
                                                            textUnderlineOffset: '2px',
                                                            wordBreak: 'break-all',
                                                            overflowWrap: 'anywhere',
                                                            transition: 'color 0.15s, text-decoration-color 0.15s',
                                                            '&:hover': isBot ? { color: '#125ea6', textDecorationColor: '#125ea6' } : {},
                                                        },
                                                        '& p': { m: 0, mb: 0.75 },
                                                        '& p:last-child': { mb: 0 },
                                                        '& ul, & ol': { m: 0, pl: 2.25, mb: 0.75 },
                                                        '& li': { mb: 0.75 },
                                                        '& li:last-child': { mb: 0 },
                                                        '& li > p': { display: 'inline', m: 0 },
                                                        '& pre': { whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowX: 'auto', m: 0 },
                                                        '& code': { wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
                                                        '& table': { display: 'block', width: '100%', overflowX: 'auto', borderCollapse: 'collapse', my: 0.75, fontSize: '0.8rem' },
                                                        '& th, & td': { border: '1px solid', borderColor: 'rgba(0,0,0,0.18)', px: 0.75, py: 0.4, textAlign: 'left' },
                                                        '& th': { fontWeight: 700, bgcolor: 'rgba(0,0,0,0.04)' },
                                                    }}
                                                >
                                                    {isBot ? (
                                                        <ReactMarkdown
                                                            remarkPlugins={[remarkGfm]}
                                                            components={{
                                                                a: ({ node, children, ...props }) => (
                                                                    <a {...props} target="_blank" rel="noreferrer">
                                                                        {children}
                                                                    </a>
                                                                ),
                                                                p: ({ node, children }) => (
                                                                    <p>
                                                                        {renderMarkdownChildrenWithSourceMarkers(
                                                                            children,
                                                                            sourceReferences,
                                                                            nextSourceReferenceIndex
                                                                        )}
                                                                    </p>
                                                                ),
                                                                li: ({ node, children }) => (
                                                                    <li>
                                                                        {renderMarkdownChildrenWithSourceMarkers(
                                                                            children,
                                                                            sourceReferences,
                                                                            nextSourceReferenceIndex
                                                                        )}
                                                                    </li>
                                                                ),
                                                            }}
                                                        >
                                                            {displayMessage}
                                                        </ReactMarkdown>
                                                    ) : (
                                                        <>{renderTextWithBracketLinks(displayMessage)}</>
                                                    )}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: 'block',
                                                        mt: 0.5,
                                                        opacity: 0.8,
                                                        textAlign: isRightSide ? 'right' : 'left',
                                                    }}
                                                >
                                                    {formatTimestamp(detail.createdAt)}
                                                </Typography>
                                                {sourceReferences.length > 0 && (
                                                    <Typography
                                                        variant="caption"
                                                        component="div"
                                                        sx={{ mt: 0.5, opacity: 0.9 }}
                                                    >
                                                        {t('agentConversation.sources', 'Sources:')}{' '}
                                                        {sourceReferences.map((reference, index) => (
                                                            <React.Fragment key={`${reference.documentId}-${reference.page}-${index}`}>
                                                                {index > 0 ? ', ' : ''}
                                                                <SourceMarker reference={reference} />
                                                            </React.Fragment>
                                                        ))}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                            <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'grey.300', mt: 1, display: 'flex', gap: 1 }}>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="application/pdf"
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelected}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder={t('agentConversation.messagePlaceholder', 'Type a message')}
                                    value={agentText}
                                    onChange={(e) => setAgentText(e.target.value)}
                                    disabled={!isConnected}
                                />
                                <IconButton
                                    onClick={handlePickFile}
                                    disabled={!isConnected || uploading}
                                    aria-label={t('agentConversation.attachPdf', 'Attach PDF')}
                                    sx={{ alignSelf: 'center' }}
                                >
                                    <AttachFileIcon />
                                </IconButton>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleConnect}
                                    disabled={isConnected}
                                >
                                    {t('agentConversation.connect', 'Connect')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleSend}
                                    disabled={!isConnected}
                                >
                                    {t('agentConversation.send', 'Send')}
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Drawer>

            <Dialog
                open={leaveConfirmOpen}
                onClose={() => setLeaveConfirmOpen(false)}
                aria-labelledby="leave-conversation-title"
            >
                <DialogTitle id="leave-conversation-title">{t('agentConversation.leaveTitle', 'Leave conversation?')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        {t('agentConversation.leaveBody', 'You are currently connected. Leaving will disconnect your WebSocket session.')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setLeaveConfirmOpen(false)}
                        disabled={leaving}
                        sx={{ textTransform: 'none' }}
                    >
                        {t('agentConversation.cancel', 'Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleLeaveConversation}
                        disabled={leaving}
                        sx={{ textTransform: 'none' }}
                    >
                        {leaving ? t('agentConversation.leaving', 'Leaving...') : t('agentConversation.leave', 'Leave')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={Boolean(error)}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseError} severity="error">
                    {error}
                </Alert>
            </Snackbar>
        </>
    );
}

export default AgentConversation;
